import React, { useEffect, useRef, useState } from 'react';
import { Modal, Form, Button, Row, Col } from 'react-bootstrap';
import { ServerPath } from '../../Constant';
import { toast } from 'react-toastify';
import { _axios } from '../../utils/axios';

const ENTITY_TYPES = [
    { value: 'TEMPLE', label: 'Temples' },
    { value: 'TEERTHA', label: 'Teerthas' },
    { value: 'DEITY', label: 'Deities' },
    { value: 'SAINT', label: 'Saints' },
    { value: 'PARIKRAMA', label: 'Parikramas' },
    { value: 'PROJECT', label: 'Projects' },
    { value: 'LIVE_DARSHAN', label: 'Live Darshan' }
];
const MAX_FAVOURITE_SELECTIONS = 10;

const CreateNotification = ({ show, onHide }) => {
    // State matching the new multi-language and scheduling requirements
    const [formData, setFormData] = useState({
        languages: { english: true, hindi: false, marathi: false, gujarati: false },
        titles: { english: '', hindi: '', marathi: '', gujarati: '' },
        descriptions: { english: '', hindi: '', marathi: '', gujarati: '' },
        status: 'Draft',
        action_link: '',
        thumbnail_url: '',
        audience_selection: 'All',
        platform_targets: [],
        activity_range: '',
        favourite_selections: [],
        isAutoScheduled: false,
        scheduledDate: '',
        scheduledTime: ''
    });
    const [searchTypeFilter, setSearchTypeFilter] = useState('');
    const [entitySearchInput, setEntitySearchInput] = useState('');
    const [entitySearchResults, setEntitySearchResults] = useState([]);
    const [isSearchingEntities, setIsSearchingEntities] = useState(false);
    const searchTimerRef = useRef(null);

    const [isSubmitting, setIsSubmitting] = useState(false);
    const [stats, setStats] = useState({}); // Initialized as object to match backend service
    const [targetUser, setTargetUser] = useState(0);

    // Fetches user statistics whenever the modal is shown
    const fetchStats = async () => {
        try {
            const { data } = await _axios.get(`${ServerPath}/admin-notification/stats`);
            if (data.success === 1) {
                setStats(data.data);
                // Default target count to Total if 'All' is selected
                if (formData.audience_selection === 'All') {
                    setTargetUser(data.data.total_notifiable_users);
                }
            }
        } catch (err) {
            console.error("Stats Fetch Error:", err);
        }
    };

    useEffect(() => {
        if (show) fetchStats();
    }, [show]);

    const handleInputChange = (e, category, lang) => {
        const { name, value, type, checked } = e.target;

        setFormData(prev => {
            // Logic for multi-language checkboxes
            if (type === 'checkbox' && category === 'languages') {
                return { ...prev, languages: { ...prev.languages, [name]: checked } };
            }
            // Logic for localized titles and descriptions
            if (category) {
                return { ...prev, [category]: { ...prev[category], [lang]: value } };
            }
            // Logic for platform selection
            if (type === 'checkbox' && name === 'platform') {
                const platforms = prev.platform_targets.includes(value)
                    ? prev.platform_targets.filter(p => p !== value)
                    : [...prev.platform_targets, value];

                // Update dynamic target count
                let count = 0;
                if (platforms.includes('iOS')) count += (stats?.ios_users || 0);
                if (platforms.includes('Android')) count += (stats?.android_users || 0);
                setTargetUser(count);

                return { ...prev, platform_targets: platforms };
            }
            return { ...prev, [name]: value };
        });
    };
    const fetchEntityResults = (type, query) => {
        if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
        setIsSearchingEntities(true);
        searchTimerRef.current = setTimeout(async () => {
            try {
                const { data } = await _axios.get(`${ServerPath}/admin-notification/favourite-entities`, {
                    params: { type, search: query || '', limit: 20 }
                });
                if (data.success === 1) setEntitySearchResults(data.data || []);
                else setEntitySearchResults([]);
            } catch (err) {
                console.error('Entity search error:', err);
                setEntitySearchResults([]);
            } finally {
                setIsSearchingEntities(false);
            }
        }, 300);
    };

    const handleEntityTypeChange = (e) => {
        setSearchTypeFilter(e.target.value);
        setEntitySearchInput('');
        setEntitySearchResults([]);
        setIsSearchingEntities(false);
    };

    const handleEntitySearchChange = (e) => {
        const value = e.target.value;
        setEntitySearchInput(value);
        if (!searchTypeFilter) return;
        fetchEntityResults(searchTypeFilter, value);
    };

    const handleEntitySelect = (entity) => {
        if (!searchTypeFilter) return;
        setFormData(prev => {
            if (prev.favourite_selections.length >= MAX_FAVOURITE_SELECTIONS) {
                toast.error(`You can select up to ${MAX_FAVOURITE_SELECTIONS} entities`);
                return prev;
            }
            if (prev.favourite_selections.some(s => s.type === searchTypeFilter && s.id === entity.entity_id)) {
                return prev; // silent dedupe
            }
            return {
                ...prev,
                favourite_selections: [
                    ...prev.favourite_selections,
                    { type: searchTypeFilter, id: entity.entity_id, name: entity.name }
                ]
            };
        });
        setEntitySearchInput('');
        setEntitySearchResults([]);
        setIsSearchingEntities(false);
    };

    const handleEntityRemove = (type, id) => {
        setFormData(prev => ({
            ...prev,
            favourite_selections: prev.favourite_selections.filter(s => !(s.type === type && s.id === id))
        }));
    };

    // Recompute target count whenever the favourite selection set changes
    useEffect(() => {
        if (formData.audience_selection !== 'Favourite') return;
        if (formData.favourite_selections.length === 0) { setTargetUser(0); return; }

        let cancelled = false;
        _axios.post(`${ServerPath}/admin-notification/favourite-entities/count-multi`, {
            selections: formData.favourite_selections.map(s => ({ type: s.type, id: s.id }))
        }).then(({ data }) => {
            if (cancelled) return;
            if (data.success === 1) setTargetUser(data.data?.favourite_user_count ?? 0);
        }).catch(err => console.error('count-multi error:', err));

        return () => { cancelled = true; };
    }, [formData.favourite_selections, formData.audience_selection]);

    const handleSubmit = async (finalStatus) => {
        const adminName = localStorage.getItem('Name');

        // 1. Dynamic Validation for Selected Languages
        const selectedLanguages = Object.keys(formData.languages).filter(lang => formData.languages[lang]);

        // Ensure English is always handled if it's your primary requirement
        if (selectedLanguages.length === 0) {
            return toast.error("Please select at least one language");
        }

        for (const lang of selectedLanguages) {
            const title = formData.titles[lang];
            const description = formData.descriptions[lang];

            if (!title || !description) {
                // Capitalize first letter for the error message
                const langLabel = lang.charAt(0).toUpperCase() + lang.slice(1);
                return toast.error(`${langLabel} Title and Description are required`);
            }
        }

        // 2. Validate Action Link
        if (!formData.action_link) {
            return toast.error("Action Link is required");
        }

        // 2a. Favourite-entity targeting validation
        if (formData.audience_selection === 'Favourite') {
            if (!searchTypeFilter && formData.favourite_selections.length === 0) {
                return toast.error("Please select an Entity Type and add at least one entity");
            }
            if (formData.favourite_selections.length === 0) {
                return toast.error("Please add at least one entity");
            }
        }

        // 3. Scheduling Validation
        if (formData.isAutoScheduled && (!formData.scheduledDate || !formData.scheduledTime)) {
            return toast.error("Please provide both Date and Time for scheduling");
        }

        try {
            setIsSubmitting(true);

            // Helper to get value only if language is selected
            const getVal = (category, lang) => formData.languages[lang] ? formData[category][lang] : null;

            const payload = {
                // Use helper to ensure only SELECTED languages are sent
                title: getVal('titles', 'english'),
                title_hin: getVal('titles', 'hindi'),
                title_mar: getVal('titles', 'marathi'),
                title_guj: getVal('titles', 'gujarati'),

                body: getVal('descriptions', 'english'),
                body_hin: getVal('descriptions', 'hindi'),
                body_mar: getVal('descriptions', 'marathi'),
                body_guj: getVal('descriptions', 'gujarati'),

                action_link: formData.action_link,
                image_url: formData.thumbnail_url,
                audience_type: formData.audience_selection === 'Platform' ? formData.platform_targets.join(',') :
                    formData.audience_selection === 'Activity' ? `Activity-${formData.activity_range}` :
                    formData.audience_selection === 'Favourite' ? 'Favourite' : 'All',
                favourite_selections: formData.audience_selection === 'Favourite'
                    ? formData.favourite_selections.map(s => ({ type: s.type, id: s.id }))
                    : null,
                status: finalStatus,
                is_scheduled: formData.isAutoScheduled ? 1 : 0,
                scheduled_at: formData.isAutoScheduled ? `${formData.scheduledDate} ${formData.scheduledTime}:00` : null,
                created_by: adminName,
                target_user: targetUser
            };

            const res = await _axios.post(`${ServerPath}/admin-notification`, payload);
            if (res.data.success) {
                toast.success(`Notification ${formData.isAutoScheduled ? 'Scheduled' : 'Published'} successfully`);
                onHide();
            }
        } catch (err) {
            console.log("Notification Creation Error:", err);
            toast.error("Failed to process notification");
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Modal show={show} onHide={onHide} size="xl" centered scrollable>
            <Modal.Header closeButton className="border-0">
                <Modal.Title className="fw-bold">Create Notification Template</Modal.Title>
            </Modal.Header>
            <Modal.Body className="px-4">

                {/* 1. Language Selection & Status */}
                <Row className="mb-4">
                    <Col md={8}>
                        <Form.Label className="fw-semibold">Select Notification Languages <span className="text-danger">*</span></Form.Label>
                        <div className="d-flex gap-4">
                            {['English', 'Hindi', 'Marathi', 'Gujarati'].map(lang => (
                                <Form.Check
                                    key={lang}
                                    type="checkbox"
                                    label={lang}
                                    name={lang.toLowerCase()}
                                    checked={formData.languages[lang.toLowerCase()]}
                                    onChange={(e) => handleInputChange(e, 'languages')}
                                />
                            ))}
                        </div>
                    </Col>
                    <Col md={4}>
                        <Form.Label className="fw-semibold">Status</Form.Label>
                        <Form.Select name="status" value={formData.status} onChange={(e) => handleInputChange(e)}>
                            <option value="Draft">Draft</option>
                            <option value="Published">Published</option>
                        </Form.Select>
                    </Col>
                </Row>

                {/* 2. Notification Titles with Stats */}
                <Form.Label className="fw-semibold mb-2">Notification Title <span className="text-danger">*</span></Form.Label>
                <Row className="g-3 mb-4">
                    {[
                        { label: 'English', key: 'english', stat: 'users_english' },
                        { label: 'Hindi', key: 'hindi', stat: 'users_hindi' },
                        { label: 'Marathi', key: 'marathi', stat: 'users_marathi' },
                        { label: 'Gujarati', key: 'gujarati', stat: 'users_gujarati' }
                    ].map(item => (
                        <Col md={3} key={item.key}>
                            <Form.Label className="small mb-1">
                                {item.label} Title <span style={{ color: '#5662F6', fontWeight: 'bold' }}>({stats[item.stat] || 0})</span>
                            </Form.Label>
                            <Form.Control
                                placeholder={`Title in ${item.label}`}
                                value={formData.titles[item.key]}
                                disabled={!formData.languages[item.key]}
                                onChange={(e) => handleInputChange(e, 'titles', item.key)}
                                maxLength={30}
                            />
                                <div className="text-end small text-muted">{formData.titles[item.key]?.length || 0}/30</div>
                        </Col>
                    ))}
                </Row>

                {/* 3. Descriptions */}
                <Form.Label className="fw-semibold mb-2">Description <span className="text-danger">*</span></Form.Label>
                <Row className="g-3 mb-4">
                    {['English', 'Hindi', 'Marathi', 'Gujarati'].map(lang => (
                        <Col md={6} key={lang}>
                            <Form.Label className="small mb-1">Description {lang}</Form.Label>
                            <Form.Control
                                as="textarea" rows={2}
                                placeholder={`Description in ${lang}`}
                                value={formData.descriptions[lang.toLowerCase()]}
                                disabled={!formData.languages[lang.toLowerCase()]}
                                maxLength={100}
                                onChange={(e) => handleInputChange(e, 'descriptions', lang.toLowerCase())}
                            />
                            <div className="text-end small text-muted">{formData.descriptions[lang.toLowerCase()]?.length || 0}/100</div>
                        </Col>
                    ))}
                </Row>

                {/* 4. Operations */}
                <Form.Label className="fw-semibold mb-2">Operations <span className="text-danger">*</span></Form.Label>
                <Row className="g-3 mb-4">
                    <Col md={6}>
                        <Form.Label className="small mb-1">Action Link / URL <span className="text-danger">*</span></Form.Label>
                        <Form.Control name="action_link" placeholder="https://..." value={formData.action_link} onChange={handleInputChange} />
                    </Col>
                    <Col md={6}>
                        <Form.Label className="small mb-1">Thumbnail Link / URL</Form.Label>
                        <Form.Control name="thumbnail_url" placeholder="https://..." value={formData.thumbnail_url} onChange={handleInputChange} />
                    </Col>
                </Row>

                {/* 5. Target Audience Grid */}
                <Form.Label className="fw-semibold mb-2">Target User (Targeting {targetUser} users)</Form.Label>
                <Row className="mb-3">
                    <Col md={4}>
                        <Form.Check type="radio" label={`All Registered (${stats?.total_notifiable_users || 0})`} name="aud" checked={formData.audience_selection === 'All'} onChange={() => { setFormData({ ...formData, audience_selection: 'All' }); setTargetUser(stats.total_notifiable_users); }} />
                    </Col>
                    <Col md={4}>
                        <Form.Check type="radio" label="By Platform" name="aud" checked={formData.audience_selection === 'Platform'} onChange={() => setFormData({ ...formData, audience_selection: 'Platform' })} />
                        <div className="ms-4 mt-2">
                            <Form.Check type="checkbox" label={`iOS (${stats?.ios_users || 0})`} checked={formData.platform_targets.includes('iOS')} disabled={formData.audience_selection !== 'Platform'} onChange={(e) => handleInputChange({ target: { name: 'platform', value: 'iOS', type: 'checkbox', checked: e.target.checked } })} />
                            <Form.Check type="checkbox" label={`Android (${stats?.android_users || 0})`} checked={formData.platform_targets.includes('Android')} disabled={formData.audience_selection !== 'Platform'} onChange={(e) => handleInputChange({ target: { name: 'platform', value: 'Android', type: 'checkbox', checked: e.target.checked } })} />
                        </div>
                    </Col>
                    <Col md={4}>
                        <Form.Check type="radio" label="By Activity" name="aud" checked={formData.audience_selection === 'Activity'} onChange={() => setFormData({ ...formData, audience_selection: 'Activity' })} />
                        <div className="ms-4 mt-2">
                            {['7', '30', '60'].map(days => (
                                <Form.Check key={days} type="radio" name="act" label={`Last ${days} Days (${stats[`active_notifiable_${days}_days`] || 0})`} disabled={formData.audience_selection !== 'Activity'} onChange={() => { setFormData({ ...formData, activity_range: days }); setTargetUser(stats[`active_notifiable_${days}_days`]); }} />
                            ))}
                        </div>
                    </Col>
                </Row>

                {/* 5a. By Favourite Entities — own row */}
                {(() => {
                    const isFav = formData.audience_selection === 'Favourite';
                    const atCap = formData.favourite_selections.length >= MAX_FAVOURITE_SELECTIONS;
                    return (
                        <Row className="g-3 mb-4 align-items-start">
                            <Col md={2} className="pt-4">
                                <Form.Check
                                    type="radio"
                                    label="By Favourite Entities"
                                    name="aud"
                                    checked={isFav}
                                    onChange={() => setFormData({ ...formData, audience_selection: 'Favourite' })}
                                />
                            </Col>
                            <Col md={3}>
                                <Form.Label className="small mb-1">Entity Type</Form.Label>
                                <Form.Select
                                    value={searchTypeFilter}
                                    onChange={handleEntityTypeChange}
                                    disabled={!isFav}
                                >
                                    <option value="" disabled hidden>Select Entity Type</option>
                                    {ENTITY_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                                </Form.Select>
                            </Col>
                            <Col md={3}>
                                <Form.Label className="small mb-1">Search Entity</Form.Label>
                                <div className="position-relative">
                                    <Form.Control
                                        type="text"
                                        placeholder={atCap ? `Max ${MAX_FAVOURITE_SELECTIONS} reached` : 'Search entity...'}
                                        value={entitySearchInput}
                                        onChange={handleEntitySearchChange}
                                        disabled={!isFav || !searchTypeFilter || atCap}
                                    />
                                    {isFav && searchTypeFilter && entitySearchInput.length > 0 && (
                                        <div
                                            className="position-absolute w-100 bg-white border rounded mt-1 shadow-sm"
                                            style={{ zIndex: 1000, maxHeight: 200, overflowY: 'auto' }}
                                        >
                                            {isSearchingEntities ? (
                                                <div className="p-2 text-muted small text-center">Searching...</div>
                                            ) : entitySearchResults.length === 0 ? (
                                                <div className="p-2 text-muted small text-center">No Result Found</div>
                                            ) : (
                                                entitySearchResults.map(e => {
                                                    const already = formData.favourite_selections.some(
                                                        s => s.type === searchTypeFilter && s.id === e.entity_id
                                                    );
                                                    return (
                                                        <div
                                                            key={e.entity_id}
                                                            className="p-2"
                                                            style={{
                                                                cursor: already ? 'not-allowed' : 'pointer',
                                                                fontSize: 14,
                                                                opacity: already ? 0.5 : 1
                                                            }}
                                                            onMouseDown={(ev) => ev.preventDefault()}
                                                            onClick={() => !already && handleEntitySelect(e)}
                                                        >
                                                            {e.name} <span className="text-muted">({e.favourite_user_count ?? 0})</span>
                                                            {already && <span className="ms-2 text-success small">added</span>}
                                                        </div>
                                                    );
                                                })
                                            )}
                                        </div>
                                    )}
                                </div>
                            </Col>
                            <Col md={4}>
                                <Form.Label className="small mb-1">
                                    Selected Entities ({formData.favourite_selections.length}/{MAX_FAVOURITE_SELECTIONS})
                                </Form.Label>
                                <div
                                    style={{
                                        minHeight: 38, maxHeight: 120, overflowY: 'auto',
                                        border: '1px solid #E2E8F0', borderRadius: 6,
                                        padding: '6px 10px', background: '#fff',
                                        display: 'flex', alignItems: 'flex-start', flexWrap: 'wrap', gap: 6
                                    }}
                                >
                                    {formData.favourite_selections.length === 0 ? (
                                        <span className="text-muted small">No entities selected</span>
                                    ) : (
                                        formData.favourite_selections.map(s => (
                                            <span
                                                key={`${s.type}:${s.id}`}
                                                className="d-inline-flex align-items-center"
                                                style={{ background: '#6366F1', color: 'white', padding: '4px 10px', borderRadius: 6, fontSize: 13 }}
                                            >
                                                {s.name || `ID: ${s.id}`}
                                                <button
                                                    type="button"
                                                    onClick={() => handleEntityRemove(s.type, s.id)}
                                                    className="ms-2 border-0 bg-transparent text-white"
                                                    style={{ padding: 0, lineHeight: 1, fontSize: 16, fontWeight: 'bold', cursor: 'pointer' }}
                                                >×</button>
                                            </span>
                                        ))
                                    )}
                                </div>
                            </Col>
                        </Row>
                    );
                })()}

                {/* 6. Scheduling */}
                <Form.Check
                    type="checkbox" label="Auto Scheduling in Future" className="fw-semibold mb-2"
                    checked={formData.isAutoScheduled}
                    onChange={(e) => setFormData({ ...formData, isAutoScheduled: e.target.checked })}
                />
                <Row className="g-3 mb-4">
                    <Col md={3}>
                        <Form.Label className="small mb-1">Date</Form.Label>
                        <Form.Control
                            type="date"
                            value={formData.scheduledDate}
                            // Prevents selecting any day before today
                            min={new Date().toISOString().split('T')[0]}
                            disabled={!formData.isAutoScheduled}
                            onChange={(e) => setFormData({ ...formData, scheduledDate: e.target.value })}
                        />
                    </Col>
                    <Col md={3}>
                        <Form.Label className="small mb-1">Time</Form.Label>
                        <Form.Control
                            type="time"
                            value={formData.scheduledTime}
                            // If the selected date is today, set min time to 'now'
                            min={
                                formData.scheduledDate === new Date().toISOString().split('T')[0]
                                    ? new Date().toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })
                                    : undefined
                            }
                            disabled={!formData.isAutoScheduled}
                            onChange={(e) => setFormData({ ...formData, scheduledTime: e.target.value })}
                        />
                    </Col>
                </Row>
            </Modal.Body>
            <Modal.Footer className="border-0 pt-0 pb-4">
                {(() => {
                    const favInvalid = formData.audience_selection === 'Favourite' && formData.favourite_selections.length === 0;
                    return (
                        <>
                            <Button className="px-5 py-2" style={{ backgroundColor: '#6c757d', border: 'none', borderRadius: '8px' }} onClick={() => handleSubmit('Draft')} disabled={isSubmitting || favInvalid}>Save as Draft</Button>
                            <Button className="px-5 py-2" style={{ backgroundColor: '#5662F6', border: 'none', borderRadius: '8px' }} onClick={() => handleSubmit('Success')} disabled={isSubmitting || favInvalid}>Publish Now</Button>
                        </>
                    );
                })()}
            </Modal.Footer>
        </Modal>
    );
};

export default CreateNotification;